import { getColor } from '@sohanscript/lib/utils';
import { ClientOnly } from '@tanstack/react-router';
import { Hydrate } from '@tanstack/react-start';
import { idle } from '@tanstack/react-start/hydration';
import { useEffect, useId, useState } from 'react';

export function MermaidBlock({ code }: { code: string }) {
	const id = useId();

	const [svg, setSvg] = useState<string | null>(null);
	const [error, setError] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setSvg(null);
		setError(false);

		// Dynamic import keeps mermaid out of the SSR bundle entirely,
		// since mermaid.initialize touches document/window at import time.
		import('mermaid')
			.then(async (mod) => {
				const mermaid = mod.default;

				mermaid.initialize({
					look: 'handDrawn',
					startOnLoad: false,
					theme: 'base',
					flowchart: {
						htmlLabels: true,
						diagramPadding: 16,
						nodeSpacing: 28,
						padding: 12,
						rankSpacing: 28,
						subGraphTitleMargin: {
							top: 12,
							bottom: 8,
						},
					},
					themeVariables: {
						fontFamily: "'Excalifont',  sans-serif",
						fontWeight: 'bold',
						fontSize: '1rem',
						background: getColor('background'),
						primaryColor: getColor('card'),
						primaryTextColor: getColor('foreground'),
						primaryBorderColor: getColor('border'),
						mainBkg: getColor('card'),
						nodeBorder: getColor('border'),
						secondaryColor: getColor('background'),
						tertiaryColor: getColor('background'),
						clusterBkg: getColor('background'),
						clusterBorder: getColor('border'),
						titleColor: getColor('foreground'),
						lineColor: getColor('secondary'),
						edgeLabelBackground: getColor('background'),
					},
				});
				await document.fonts.ready;
				const { svg: rendered } = await mermaid.render(`diagram-${id}`, code);
				if (!cancelled) setSvg(rendered);
			})
			.catch(() => {
				if (!cancelled) setError(true);
			});

		return () => {
			cancelled = true;
		};
	}, [code, id]);

	return (
		<ClientOnly>
			<Hydrate when={idle()}>
				<div className="overflow-x-auto p-4">
					{svg ? (
						<div
							className="mermaid-diagram *:mx-auto"
							dangerouslySetInnerHTML={{ __html: svg }}
						/>
					) : (
						<pre
							className={
								error
									? 'text-destructive text-xs'
									: 'text-foreground/60 text-xs'
							}
						>
							<code>{code}</code>
						</pre>
					)}
				</div>
			</Hydrate>
		</ClientOnly>
	);
}
